import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import {
    KeyboardArrowDown,
    EditOutlined,
    AccountBalanceWalletOutlined,
    PaidOutlined,
    RemoveCircleOutline,
    HistoryOutlined,
    BlockOutlined,
    CheckCircleOutline
} from '@mui/icons-material';

// Row Actions Button Dropdown
const RowActionDropdown = ({
    row,
    onOpenSetLimit,
    onOpenSettleModal,
    onOpenCreditBalanceModal,
    onOpenDebitBalanceModal,
    onOpenHistoryModal,
    onOpenStatusModal
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const outstanding = Number(row?.current_outstanding ?? row?.outstanding ?? row?.outstanding_amount ?? 0);
    const isActive = String(row?.status || 'Active').toLowerCase() === 'active';

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                endIcon={<KeyboardArrowDown sx={{ fontSize: 16 }} />}
                onClick={handleClick}
                sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 12,
                    py: 0.35,
                    px: 1.25,
                    color: open ? 'primary.main' : 'text.primary',
                    borderColor: open ? 'primary.main' : 'divider',
                    bgcolor: open ? '#eff6ff' : '#fff',
                    '&:hover': {
                        bgcolor: '#f8fafc',
                        borderColor: 'primary.main'
                    }
                }}
            >
                Actions
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        minWidth: 195,
                        borderRadius: 2,
                        mt: 0.5,
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: 'divider',
                        py: 0.5
                    }
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenSetLimit(row);
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                        <EditOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}>Edit Limit</ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenSettleModal(row);
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon sx={{ minWidth: 32, color: outstanding > 0 ? '#d97706' : 'text.disabled' }}>
                        <AccountBalanceWalletOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}>Settle Repayment</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenCreditBalanceModal(row);
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon sx={{ minWidth: 32, color: '#16a34a' }}>
                        <PaidOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>Credit Balance</ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenDebitBalanceModal(row);
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon sx={{ minWidth: 32, color: '#dc2626' }}>
                        <RemoveCircleOutline fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>Debit Balance</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                    onClick={() => {
                        handleClose();
                        if (onOpenHistoryModal) onOpenHistoryModal(row);
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon sx={{ minWidth: 32, color: '#0284c7' }}>
                        <HistoryOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#0369a1' }}>
                        Adjustment History
                    </ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {isActive ? (
                    <MenuItem
                        onClick={() => {
                            handleClose();
                            if (onOpenStatusModal) onOpenStatusModal(row, 'inactive');
                        }}
                        sx={{ py: 1 }}
                    >
                        <ListItemIcon sx={{ minWidth: 32, color: '#dc2626' }}>
                            <BlockOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>
                            Deactivate Credit Access
                        </ListItemText>
                    </MenuItem>
                ) : (
                    <MenuItem
                        onClick={() => {
                            handleClose();
                            if (onOpenStatusModal) onOpenStatusModal(row, 'active');
                        }}
                        sx={{ py: 1 }}
                    >
                        <ListItemIcon sx={{ minWidth: 32, color: '#16a34a' }}>
                            <CheckCircleOutline fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>
                            Activate Credit Access
                        </ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

RowActionDropdown.propTypes = {
    row: PropTypes.object.isRequired,
    onOpenSetLimit: PropTypes.func.isRequired,
    onOpenSettleModal: PropTypes.func.isRequired,
    onOpenCreditBalanceModal: PropTypes.func.isRequired,
    onOpenDebitBalanceModal: PropTypes.func.isRequired,
    onOpenHistoryModal: PropTypes.func,
    onOpenStatusModal: PropTypes.func
};

export default RowActionDropdown;
